import {
  ComponentType,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useMemo,
} from 'react'
import { queries, Queries } from '@testing-library/dom'
import {
  render,
  renderHook,
  RenderOptions,
  RenderResult,
} from '@testing-library/react'
import { DeepPartial } from 'utility-types'
import {
  generateMockAuthData,
  generateMockPageData,
  generateMockUserData,
} from '@skedulo/horizon-toolkit'
import { ObjectLike } from '@skedulo/horizon-types'
import { Page } from '@skedulo/protobuf-web/dist/ui/crud'
import {
  ApiClientContextProvider,
  ApiClientV1,
  PageContext,
  UserContext,
  UserMetadata,
} from './core'

export interface WithMockedApiClientProps {
  mockedApiClient?: DeepPartial<ApiClientV1>
}
//
/**
 * Generates a real ApiClient instance with mock auth data.
 * This can then be used to spy on and return a mocked response when passed into the API client provider
 *
 * e.g.
 * const apiClient = generateApiClient()
 * jest.spyOn(apiClient.viewState, 'listViewTypes').mockResolvedValue({ viewTypes: [], totalCount: 0 })
 */
export const generateApiClient = () => {
  return new ApiClientV1({
    auth: generateMockAuthData(),
    platformApiDomain: '/api',
  })
}

/**
 * Component wrapper to provide a mocked ApiClient via props.
 */
export const withMockedApiClient = <Props extends WithMockedApiClientProps>(
  Component: ComponentType<Props>,
  mockedApiClient: DeepPartial<ApiClientV1>,
) => {
  return (props: Omit<Props, keyof WithMockedApiClientProps>) => {
    return <Component mockedApiClient={mockedApiClient} {...(props as Props)} />
  }
}

/**
 * Test wrapper to provide a mock ApiClient via context.
 *
 * Uses a real ApiClient instance by default, which will attempt to call through
 * to the underlying clients from `@skedulo/protobuf-web` and typically fail
 * unless you are mocking this module.
 *
 * A mocked implementation of the ApiClient can be provided with a prop, which
 * you'll usually want to do via the `withMockedApiClient` wrapper.
 */
export const MockApiClientProvider = ({
  children,
  mockedApiClient,
}: PropsWithChildren<WithMockedApiClientProps>) => {
  const apiClient = (mockedApiClient as ApiClientV1) ?? generateApiClient()

  return (
    <ApiClientContextProvider value={apiClient}>
      {children}
    </ApiClientContextProvider>
  )
}

/**
 * Test wrapper to provide mock Page data via context.
 */
export const MockPageProvider = ({
  page = generateMockPageData(),
  children,
}: {
  page?: Page
  children: ReactNode
}) => {
  const value = useMemo(() => ({ page }), [page])

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}

/**
 * Test wrapper to provide mock User data via context.
 *
 * Pass `metadata` to exercise code paths that read the richer `/custom/usermetadata`
 * fields (timezone, role flags, resourceId, ...). Left `undefined` by default
 * to keep consumers explicit about whether they exercise that code.
 */
export const MockUserProvider = ({
  user = generateMockUserData(),
  metadata,
  children,
}: {
  user?: ObjectLike
  metadata?: UserMetadata
  children: ReactNode
}) => {
  const value = useMemo(
    () => ({ user, metadata, loading: false, metadataLoading: false }),
    [user, metadata],
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

/**
 * Test wrapper to provide all contexts.
 */
export const AllTheProviders = ({
  children,
  apiClient,
}: {
  children: ReactNode
  apiClient?: DeepPartial<ApiClientV1>
}) => {
  return (
    <MockApiClientProvider mockedApiClient={apiClient}>
      <MockUserProvider>
        <MockPageProvider>{children}</MockPageProvider>
      </MockUserProvider>
    </MockApiClientProvider>
  )
}
//
/**
 * Wrapper around `@testing-library/react::render` function, providing all
 * contexts to the component(s) under test.
 */
export const renderWithContext = <
  Q extends Queries = typeof queries,
  Container extends Element | DocumentFragment = HTMLElement,
  BaseElement extends Element | DocumentFragment = Container,
>(
  ui: ReactElement,
  options?: RenderOptions<Q, Container, BaseElement>,
): RenderResult<Q, Container, BaseElement> => {
  return render<Q, Container, BaseElement>(ui, {
    wrapper: AllTheProviders,
    ...options,
  })
}

/**
 * This function will not work in a React 17 app that uses @testing-library/react-hooks
 * due to renderHook not being available in @testing-library/react V12.
 * It will have to be copied and pasted into a React 17 app pointing to the correct import
 *
 * This helper can only be used internally in this Horizon Framework repo at the moment.
 *
 * To pass in an API client to mock API calls, pass it in as an option like this:
 * const apiClient = generateApiClient()
 * jest.spyOn(apiClient.viewState, 'listViewTypes').mockResolvedValue({ viewTypes: [], totalCount: 0 })
 * renderHookWithContext(() => useHook(), { apiClient })
 */
export const renderHookWithContext: typeof renderHook = (hook, options) => {
  return renderHook(hook, {
    wrapper: ({ children }) => <AllTheProviders>{children}</AllTheProviders>,
    ...options,
  })
}
